import { Colors, Lightning } from '@lightningjs/sdk';
import { constants } from 'aliases';
import { debounce, getImageTextureObj } from 'support/generalUtils';
import { translate } from 'support/translate';

const SELECTION_INDICATOR_SIZE = 48;
const SELECTION_INDICATOR_Y = 16;
const PADDING = 23;
const ICON_SIZE = 32;

const CHECKMARK_ICON = getImageTextureObj(
  'static/images/check-dark.svg',
  ICON_SIZE,
  ICON_SIZE,
);

export interface TileSelectOverlayTemplateSpec
  extends Lightning.Component.TemplateSpec {
  Content: {
    TileOverlay: Lightning.Component;
    SelectionIndicator: {
      Icon: Lightning.textures.ImageTexture;
    };
  };
}

export default class TileSelectOverlay extends Lightning.Component<TileSelectOverlayTemplateSpec> {
  private _Content = this.getByRef('Content')!;
  private _TileOverlay = this._Content.getByRef('TileOverlay')!;
  private _SelectionIndicator = this._Content.getByRef('SelectionIndicator')!;
  private _Icon = this._SelectionIndicator.getByRef('Icon')!;

  private _manualAnnounce = debounce(
    this.announceSelection.bind(this),
    constants.timers.manualAnnounceDelay,
  );

  override set w(w: number) {
    this._TileOverlay.patch({ w });
    this._SelectionIndicator.patch({
      x: w - PADDING - SELECTION_INDICATOR_SIZE,
    });
  }

  override set h(h: number) {
    this._TileOverlay.patch({ h });
  }

  set isSelected(isSelected: boolean) {
    this.toggleSelection(isSelected);
    this.fireAncestors('$announcerCancel');
    this._manualAnnounce(isSelected);
  }

  static override _template(): Lightning.Component.Template<TileSelectOverlayTemplateSpec> {
    return {
      Content: {
        TileOverlay: {
          rect: true,
          w: (w: number) => w,
          h: (h: number) => h,
          color: Colors('black').alpha(0.5).get(),
          visible: false,
        },
        SelectionIndicator: {
          w: SELECTION_INDICATOR_SIZE,
          h: SELECTION_INDICATOR_SIZE,
          y: SELECTION_INDICATOR_Y,
          zIndex: constants.ui.zIndex.foregroundLayer + 3,
          rect: true,
          color: Colors('black').alpha(0.5).get(),
          shader: {
            type: Lightning.shaders.RoundedRectangle,
            radius: 24,
            stroke: 3,
            strokeColor: Colors('white').get(),
          },
          Icon: {
            visible: false,
            ...CHECKMARK_ICON,
            x: (SELECTION_INDICATOR_SIZE - ICON_SIZE) / 2,
            y: (SELECTION_INDICATOR_SIZE - ICON_SIZE) / 2,
          },
        },
      },
    };
  }

  private toggleSelection(isSelected: boolean) {
    this._TileOverlay.patch({ visible: isSelected });
    this._SelectionIndicator.patch({
      color: isSelected
        ? Colors('white').get()
        : Colors('black').alpha(0.5).get(),
    });
    this._Icon.patch({ visible: isSelected });
  }

  private announceSelection(isSelected: boolean) {
    this.fireAncestors(
      '$announce',
      isSelected
        ? translate('ttsPrompts.tileSelectOverlay.selected')
        : translate('ttsPrompts.tileSelectOverlay.unselected'),
    );
  }
}
